import React from 'react';
import styled from '@emotion/styled';
import { IVehicle } from '../../../shared/api/gateway/models/Vehicle';
import { useTranslation } from '../../../shared/i18n/translate/useTranslation';
import { Color } from '../../../shared/styles/Color';
import { CarIcon, Css, H3, H4, PlusIcon, TextButton } from '../../../../ui';
import { theme } from '../../../shared/styles';

interface IVehiclesListProps {
	vehiclesList: IVehicle[];
	onAddVehicle?: () => void;
	editVehicle?: (vehicle: IVehicle) => void;
	addVehicleButtonText?: string;
	isBookingDetailPage?: boolean;
	selectedVehicle?: IVehicle | null;
	setSelectedVehicle?: (vehicle: IVehicle) => void;
}

export const VehiclesList = ({
	vehiclesList,
	onAddVehicle,
	editVehicle,
	addVehicleButtonText,
	isBookingDetailPage,
	selectedVehicle,
	setSelectedVehicle,
}: IVehiclesListProps) => {
	const { t } = useTranslation();

	// Show "Add New Vehicle" tile only if vehicle count is below or equal to this threshold
	const SHOW_ADD_NEW_VEHICLE_TILE_THRESHOLD = 3;

	const getVehicleCardButtonTitle = (vehicle: IVehicle) => {
		if (isBookingDetailPage) {
			if (selectedVehicle?.vehicleId === vehicle.vehicleId) {
				return t('parking_personal_details_vehicle_details_selected_vehicle');
			}

			return t('parking_personal_details_vehicle_details_select_vehicle');
		}

		return t('account_vehicles_edit_vehicle');
	};

	return (
		<>
			{vehiclesList?.map((vehicle) => {
				const { vehicleId, description, licensePlate, favorite } = vehicle;
				const cardButtonTitle = getVehicleCardButtonTitle(vehicle);
				const isSelected = selectedVehicle?.vehicleId === vehicle.vehicleId;

				return (
					<VehicleCard
						key={vehicleId}
						selected={isSelected}
						isBookingDetailPage
					>
						<VehicleCardRow>
							{!isBookingDetailPage && (
								<div>
									<StyledCarIcon />
								</div>
							)}
							<VehicleDetails>
								<VehicleTitle>{description}</VehicleTitle>
								<VehiclePlate>
									{t('account_vehicles_license_plate')} {licensePlate}
								</VehiclePlate>
							</VehicleDetails>
							{favorite && (
								<FavoriteText>{t('account_vehicles_default')}</FavoriteText>
							)}
						</VehicleCardRow>
						<Divider selectedVehicle={selectedVehicle} />
						<VehicleTextButton
							type="button"
							onClick={
								isBookingDetailPage
									? () => setSelectedVehicle?.(vehicle)
									: () => editVehicle?.(vehicle)
							}
						>
							<span>{cardButtonTitle}</span>
						</VehicleTextButton>
					</VehicleCard>
				);
			})}
			{vehiclesList.length <= SHOW_ADD_NEW_VEHICLE_TILE_THRESHOLD &&
				!isBookingDetailPage && (
					<AddVehicleButton onClick={onAddVehicle}>
						<PlusIcon />
						<p>{addVehicleButtonText}</p>
					</AddVehicleButton>
				)}
		</>
	);
};

const VehicleCard = styled.div<{
	selected?: boolean;
	isBookingDetailPage?: boolean;
}>`
	background: ${Color.Surface};
	border-radius: 5px;
	${({ selected, isBookingDetailPage }) =>
		selected &&
		isBookingDetailPage &&
		`box-shadow: inset 0 0 0 2px ${Color.PrimaryDefault}`};
`;

const VehicleCardRow = styled.div`
	display: flex;
	gap: 22px;
	padding: 22px;

	p {
		margin: 0;
	}
`;

const StyledCarIcon = styled(CarIcon)`
	width: 25px;
	height: 20px;

	path {
		fill: ${Color.SecondaryDark};
	}
`;

const VehicleDetails = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;

	p {
		line-height: normal;
	}
`;

const VehicleTitle = styled(H4.withComponent(H3))`
	color: ${Color.Background1};
	margin-bottom: 0;
`;

const VehiclePlate = styled.p`
	color: ${Color.Surface2};
`;

const FavoriteText = styled.p`
	color: ${Color.Surface2};
`;

const Divider = styled.hr<{ selectedVehicle?: IVehicle | null }>`
	margin: ${({ selectedVehicle }) =>
		selectedVehicle ? '0 2px' : '0'} !important;
`;

export const VehicleTextButton = styled(TextButton)`
	width: 100%;
	height: 75px;
	color: ${Color.PrimaryDark};
	font-weight: 400;
	font-size: ${Css.pxToRem(21)};
	justify-content: center;
	cursor: pointer;

	span {
		position: relative;
		display: inline-block;

		&::after {
			content: '';
			position: absolute;
			bottom: -7px;
			left: 0;
			width: 0%;
			height: 2px;
			background: ${Color.PrimaryDefault};
			transition: width 0.2s ease;
		}
	}

	&:hover {
		color: ${Color.PrimaryDefault};
	}

	&:hover span::after {
		width: 100%;
	}

	&:focus-visible {
		outline: ${theme.button?.primary?.focus?.outline};
		border-radius: 5px;
	}
`;

const AddVehicleButton = styled(TextButton)`
	height: 180px;
	padding: 20px;
	color: ${Color.PrimaryDefault};
	font-weight: 400;
	font-size: ${Css.pxToRem(21)};

	// https://kovart.github.io/dashed-border-generator/ - generated a custom border.
	background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='5' ry='5' stroke='%23B3B3B3FF' stroke-width='3' stroke-dasharray='6' stroke-dashoffset='4' stroke-linecap='square'/%3e%3c/svg%3e");
	border-radius: 5px;

	transition: background-color 0.2s ease-in;

	p {
		margin-top: 25px;
	}

	&:hover {
		background-color: ${Color.Surface};
		color: ${Color.PrimaryDefault};
	}
`;
